import { memo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { useDeleteHousehold } from "@/hooks/api/useHouseholds"
import type { DeleteHouseholdProps } from "@/types/household"
import { Button, Text } from "@chakra-ui/react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"

const DeleteHousehold = memo(
  ({ household, onSuccess, onCancel }: DeleteHouseholdProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteHouseholdMutation = useDeleteHousehold()

    const displayLabel = household.user_id ?? household.id

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleConfirm = async () => {
      try {
        await deleteHouseholdMutation.mutateAsync(household.id)
        setIsOpen(false)
        onSuccess?.()
      } catch (error) {
        // Keep dialog open so user can retry after resolving error
        console.error("Error deleting household:", error)
      }
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          colorPalette="red"
          aria-label={`Delete household: ${displayLabel}`}
        >
          <FiTrash2 fontSize="14px" aria-hidden="true" />
          Delete
        </Button>

        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title="Delete Household"
          message={
            <>
              <Text>
                Are you sure you want to delete <strong>{displayLabel}</strong>?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This action cannot be undone. This household will be permanently
                removed.
              </Text>
            </>
          }
          confirmText="Delete Household"
          confirmColorScheme="red"
          isLoading={deleteHouseholdMutation.isPending}
        />
      </>
    )
  },
)

DeleteHousehold.displayName = "DeleteHousehold"

export default DeleteHousehold
export type { DeleteHouseholdProps }
